const LOCAL_API_URL = 'http://localhost:8002/api/v1'
const PRODUCTION_API_URL =
  import.meta.env.VITE_API_BASE_URL?.trim() ||
  'https://silverfoil.napps.in/aonlastay-api/api/v1'

/** Use local FastAPI on localhost unless VITE_API_BASE_URL is set. */
export function resolveApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_BASE_URL?.trim()
  if (envUrl) return envUrl.replace(/\/$/, '')
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1') {
      return LOCAL_API_URL
    }
  }
  return PRODUCTION_API_URL
}

export const API_CONFIG = {
  get BASE_URL() {
    return resolveApiBaseUrl()
  },
  ENDPOINTS: {
    ROOMS: '/rooms',
    ROOM_DETAILS: '/rooms',
    ROOM_SEARCH: '/rooms/search',
    ROOM_CATEGORY: '/rooms/category',
    BOOKINGS: '/bookings',
    LOCATIONS: '/locations',
    SEARCH_CITIES: '/search/cities',
    SEARCH_DEFAULTS: '/search/defaults',
    AUTH: {
      LOGIN: '/login',
      REGISTER: '/register',
      LOGOUT: '/logout',
      USER: '/user'
    }
  },
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3
}

// Image Configuration — empty = same origin (Laravel /uploads, /assets, …)
export const IMAGE_CONFIG = {
  BASE_URL: import.meta.env.VITE_IMAGE_BASE_URL ?? '',
  FALLBACK_IMAGE: 'https://via.placeholder.com/600x400?text=No+Image',
  THUMBNAIL_FALLBACK: 'https://via.placeholder.com/80x80?text=No+Image'
}

// Helper function to get full image URL
export const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return IMAGE_CONFIG.FALLBACK_IMAGE
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath
  }
  
  // Check if Firebase Storage URL is embedded in the path (e.g., "/uploads/room-images/https://storage.googleapis.com/...")
  const firebaseUrlMatch = imagePath.match(/https?:\/\/[^\s]+/i)
  if (firebaseUrlMatch) {
    return firebaseUrlMatch[0]
  }
  
  // If it starts with '/', add the base URL
  if (imagePath.startsWith('/')) {
    return `${IMAGE_CONFIG.BASE_URL}${imagePath}`
  }
  
  // Otherwise, assume it's a relative path and add the base URL
  return `${IMAGE_CONFIG.BASE_URL}/${imagePath}`
}

// Request headers
export const getHeaders = (includeAuth = false) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }

  if (includeAuth) {
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  return headers
}

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  errors?: Record<string, string[]>
}

export interface PaginatedResponse<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number
  to: number
}

// Room related types
export interface RoomData {
  id: number
  room_id: number
  title: string
  slug: string
  summary: string
  rent: number
  bed: number
  bath: number
  max_guests: number
  featured_img: string
  slider_imgs?: string | string[]
  vendor_id?: number
  vendor_username?: string
  category_id?: number
  category_name?: string
  amenities?: string[]
  rating?: number
  reviews_count?: number
  distance_km?: number
  created_at: string
  updated_at: string
}

export interface RoomFilters {
  category?: string
  dates?: string
  checkin?: string
  checkout?: string
  location?: string
  lat?: number | string
  lng?: number | string
  radius_km?: number | string
  beds?: string
  baths?: string
  guests?: string
  sort_by?: string
  rents?: string
  price_min?: number
  price_max?: number
  ammenities?: string[]
  page?: number
  per_page?: number
}

export interface RoomSearchParams extends RoomFilters {
  checkin?: string
  checkout?: string
  location?: string
  price_min?: number
  price_max?: number
}
